import { useEffect, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Location } from 'history';

import {
  NETWORK_ERROR_MESSAGE,
  SERVER_ERROR_MESSAGE,
  SITTER_PROFILE_EVENTSTREAM_EVENTS,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
import Favorite from '@rover/icons/svg/favorite.svg';
import FavoriteFill from '@rover/icons/svg/favorite-fill.svg';
import { Button } from '@rover/kibble/core';
import { Props as ButtonProps } from '@rover/kibble/core/Button';
import useTheme from '@rover/kibble/themes/useTheme';
import { useApiRelationshipDetailUpdate } from '@rover/rsdk/src/apiClient/latest/api-relationship-detail-update';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import { NetworkError } from '@rover/rsdk/src/errors';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';
import { redirect } from '@rover/utilities/url';

type Props = {
  sitterInfo: EnhancedPersonSitterProfile | undefined;
  isLoggedIn?: boolean;
  onError?: (string) => void;
  location: Location;
  size?: ButtonProps['size'];
};

const StyledFavoriteFill = (props): JSX.Element => {
  const theme = useTheme();
  return <FavoriteFill {...props} fill={theme.colors.brand.active_brand} />;
};

const FavoriteButton = ({
  sitterInfo,
  isLoggedIn,
  onError,
  location,
  size,
}: Props): JSX.Element => {
  const { isError, isLoading, mutate, error } = useApiRelationshipDetailUpdate({
    mutation: { retry: 3 },
  });

  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  useEffect(() => {
    setIsFavorite(!!sitterInfo?.authenticatedContext?.isFavorite);
  }, [sitterInfo]);

  useEffect(() => {
    if (isError) {
      if (!onError) return;
      const isNetworkError = error instanceof NetworkError;
      const message = isNetworkError ? NETWORK_ERROR_MESSAGE() : SERVER_ERROR_MESSAGE();
      onError(message);
    }
  }, [isError]);

  const handleOnPressFavorite = (): void => {
    setIsFavorite(!isFavorite);
    emitMetric('sitter_profile_favorite_button_clicked');
    Logger.info('sitter_profile_favorite_button_clicked');
    if (!isLoggedIn) {
      redirect(`/account/continue/?next=${location.pathname}`);
      return;
    }
    fireDataLayerEvent({
      event: isFavorite
        ? SITTER_PROFILE_EVENTSTREAM_EVENTS.REMOVE_FROM_FAVORITES
        : SITTER_PROFILE_EVENTSTREAM_EVENTS.SAVE_TO_FAVORITES,
    });

    mutate({
      opk: sitterInfo!.opk,
      data: { isFavorite: !isFavorite },
      viewerType: 'requester',
    });
  };

  const buttonIcon = isFavorite ? StyledFavoriteFill : Favorite;

  return (
    <Button
      icon={buttonIcon}
      loading={isLoading}
      onPress={handleOnPressFavorite}
      circular
      aria-label={t`Save to favorites`}
      aria-pressed={isFavorite ? 'true' : 'false'}
      data-testid="favorite-button"
      size={size}
      sx={{
        '&:focus': {
          backgroundColor: 'initial',
        },
      }}
    />
  );
};

export default FavoriteButton;
